const FALLBACK_DISPLAY_NAME = "Authenticated user";
const SYNTHETIC_CMMD_USERNAME_PATTERN = /^user:\d+$/i;

export function resolveAccountDisplayName(input: string | null | undefined): string {
  const value = input?.trim();
  if (!value) {
    return FALLBACK_DISPLAY_NAME;
  }
  return value;
}

export function resolveCmmdPlanLabel(input: string | null | undefined): string | null {
  const value = input?.trim();
  return value && value.length > 0 ? value : null;
}

export function resolveCmmdIdentityDisplayName(
  identity: {
    readonly firstName: string | null;
    readonly lastName: string | null;
    readonly username: string;
    readonly email: string;
  } | null,
): string | null {
  if (!identity) return null;
  const firstName = identity.firstName?.trim() ?? "";
  const lastName = identity.lastName?.trim() ?? "";
  if (firstName.length > 0 && lastName.length > 0) {
    return `${firstName} ${lastName[0]!.toUpperCase()}.`;
  }
  if (firstName.length > 0) return firstName;
  const username = identity.username.trim();
  const email = identity.email.trim();
  if (username.length > 0 && !SYNTHETIC_CMMD_USERNAME_PATTERN.test(username)) return username;
  if (email.length > 0 && !SYNTHETIC_CMMD_USERNAME_PATTERN.test(email)) return email;
  return null;
}

/**
 * The rail's account row shows one identity on one line, so the name has to fit
 * beside an avatar and a feedback button at the sidebar's narrowest width.
 * CMMD identities already arrive compacted by
 * {@link resolveCmmdIdentityDisplayName}; this applies the same `First L.` shape
 * to the primary-account fallback so the row does not change form depending on
 * which source answered.
 *
 * Emails and the unauthenticated placeholder pass through untouched — an email
 * is an identity rather than a name, and "Authenticated u." would read as a
 * truncation bug.
 */
export function resolveShortAccountName(displayName: string): string {
  const value = displayName.trim();
  if (value.length === 0 || value === FALLBACK_DISPLAY_NAME || value.includes("@")) {
    return value.length === 0 ? FALLBACK_DISPLAY_NAME : value;
  }

  const segments = value.split(/\s+/u).filter((segment) => segment.length > 0);
  if (segments.length < 2) return value;

  const lastInitial = segments.at(-1)![0]!;
  return `${segments[0]!} ${lastInitial.toUpperCase()}.`;
}

export function resolveAccountInitials(displayName: string): string {
  const segments = displayName
    .trim()
    .split(/\s+/)
    .filter((segment) => segment.length > 0);

  if (segments.length === 0) {
    return "AU";
  }

  if (segments.length === 1) {
    return segments[0]!.slice(0, 2).toUpperCase();
  }

  return `${segments[0]![0] ?? ""}${segments[1]![0] ?? ""}`.toUpperCase();
}

export function resolveAccountRoleLabel(role: "owner" | "client", verified: boolean): string {
  const roleLabel = role === "owner" ? "Owner" : "Client";
  return `${roleLabel} · ${verified ? "CMMD" : "Fallback"}`;
}

function nonEmptyAvatarUrl(input: string | null | undefined): string | null {
  const value = input?.trim();
  return value && value.length > 0 ? value : null;
}

export function resolveAccountAvatarUrl(input: {
  readonly connected: boolean;
  readonly cmmdAvatarUrl: string | null | undefined;
  readonly localAvatarUrl: string | null | undefined;
}): string | null {
  if (input.connected) {
    const cmmdAvatar = nonEmptyAvatarUrl(input.cmmdAvatarUrl);
    if (cmmdAvatar) return cmmdAvatar;
  }
  return nonEmptyAvatarUrl(input.localAvatarUrl);
}

export function shouldRefreshPrimaryAccountIdentity(input: {
  readonly connected: boolean;
  readonly cmmdAvatarUrl: string | null | undefined;
  readonly localAvatarUrl: string | null | undefined;
}): boolean {
  return input.connected && resolveAccountAvatarUrl(input) === null;
}
